import { Link } from "react-router-dom";
import { ArrowRight, Check, X, Camera, Barcode, Stack, FolderSimple, Warning } from "@phosphor-icons/react";
import { MarketingFrame } from "../components/MarketingFrame";

const ROWS = [
  ["When is the file named?", "After the shoot, from thumbnails", "The instant the shutter fires"],
  ["How is the SKU attached?", "Guessed by AI from pixels, or typed by hand", "Locked per session before the first shot"],
  ["Multi-angle order (front / back / detail…)", "Re-sorted by eye, angle 7 vs 8 easy to swap", "Sequence number increments automatically"],
  ["Near-identical variants (S / M / L, colourways)", "Visual matchers misclassify look-alikes", "Variant is in the session name — pixels never matter"],
  ["Where does the metadata live?", "Filename only, lost on re-export", "Filename AND EXIF, survives every tool downstream"],
  ["Work per 90-file drop", "Open, inspect, rename, verify: 90 decisions", "0 decisions after setup"],
  ["Mistake surface", "Rename an entire folder wrong", "Wrong session = visible before the shot"],
];

const STEPS = [
  { icon: Barcode, t: "LOCK THE SKU", d: "Create a session: SKU prefix, base tags (brand, colour, size), starting sequence." },
  { icon: Camera, t: "SHOOT THE ANGLES", d: "Front, back, side, detail. Each tap writes SKU-0001, SKU-0002… with tags baked into EXIF." },
  { icon: Stack, t: "NEXT VARIANT", d: "Duplicate the session, change 'Blue' to 'Red'. Shoot again. Nothing to sort later." },
  { icon: FolderSimple, t: "EXPORT OR SYNC", d: "Files leave already named and tagged — drop them straight into your Shopify CSV image columns or sync to AIImageSuite.pro." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Why SKU-Locked Naming at Capture Beats Post-Hoc File Renaming",
  url: "https://aiimagesuites.pro/compare/capture-time-naming-vs-renaming",
  author: { "@type": "Organization", name: "AIImageSuites" },
  about: ["batch photo renaming", "SKU matching", "EXIF metadata", "e-commerce product photography"],
};

export default function ComparePage() {
  return (
    <MarketingFrame
      testid="compare-page"
      title="Capture-Time Naming vs. Post-Hoc Renamers (ShortPixel, RenamerX) — AIImageSuites"
      description="Why naming and SKU-locking product photos at capture beats renaming 90+ files after the shoot. Multi-angle, multi-variant e-commerce drops without downstream sorting."
      jsonLd={JSONLD}
    >
      <article className="px-6 py-14 max-w-5xl mx-auto w-full space-y-12">
        <header className="space-y-4">
          <p className="label-mono text-neutral-500">COMPARISON · CAPTURE-TIME NAMING VS. POST-HOC RENAMERS</p>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95]" data-testid="compare-headline">
            WHY SKU-LOCKED NAMING <span className="text-[#FF2A2A]">AT CAPTURE</span> BEATS RENAMING FILES AFTER THE SHOOT.
          </h1>
          <p className="font-mono text-neutral-700 max-w-3xl">
            Post-hoc renamers — ShortPixel's AI renamer, RenamerX, folder batch scripts — all start from the same place:
            a pile of <span className="font-bold">IMG_4821.JPG</span> files and a human (or a vision model) guessing which
            product, which variant and which angle each one is. Capture-time naming removes the guess by never creating the pile.
          </p>
        </header>

        <section className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="compare-problem">
          <div className="border-2 border-black p-6 bg-[#F8F9FA]">
            <div className="flex items-center gap-2 font-display font-bold tracking-tighter text-lg"><Warning size={20} weight="bold" className="text-[#FF2A2A]" /> THE POST-HOC PROBLEM</div>
            <ul className="mt-3 space-y-2 text-sm font-mono text-neutral-700">
              <li className="flex gap-2"><X size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /> A 30-SKU drop at 3 angles is 90 files to open, inspect and rename — one merchant's exact words in a Shopify thread this month.</li>
              <li className="flex gap-2"><X size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /> AI SKU-matching reads pixels. Size S and size M of the same tee are pixel-identical; so are two colourways under warm light.</li>
              <li className="flex gap-2"><X size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /> Angle order is reconstructed from thumbnails. Swap back and detail once and the wrong hero image ships.</li>
              <li className="flex gap-2"><X size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /> The result lives only in the filename. Export through one more tool and it's gone.</li>
            </ul>
          </div>
          <div className="border-2 border-black p-6 bg-black text-white">
            <div className="flex items-center gap-2 font-display font-bold tracking-tighter text-lg"><Camera size={20} weight="bold" className="text-[#FF2A2A]" /> THE CAPTURE-TIME MOAT</div>
            <ul className="mt-3 space-y-2 text-sm font-mono text-neutral-200">
              <li className="flex gap-2"><Check size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /><span>The session locks SKU, variant and tags <b>before</b> the first shutter press.</span></li>
              <li className="flex gap-2"><Check size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /><span>Every shot is written as <span className="font-bold">SKU-VARIANT-0007.jpg</span> with the same data baked into EXIF.</span></li>
              <li className="flex gap-2"><Check size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /> Angle order is the sequence number. It cannot be mis-sorted because it was never sorted.</li>
              <li className="flex gap-2"><Check size={16} weight="bold" className="text-[#FF2A2A] shrink-0 mt-0.5" /> Downstream catalog sorting disappears — there is nothing left to sort.</li>
            </ul>
          </div>
        </section>

        <section data-testid="compare-table">
          <p className="label-mono text-neutral-500 mb-4">SIDE BY SIDE</p>
          <div className="border-2 border-black overflow-x-auto">
            <table className="w-full text-sm font-mono min-w-[640px]">
              <thead className="bg-neutral-100 border-b-2 border-black label-mono text-xs">
                <tr><th className="text-left p-3">QUESTION</th><th className="text-left p-3">POST-HOC RENAMER</th><th className="text-left p-3 bg-black text-white">AIIMAGESUITES CAPTURE</th></tr>
              </thead>
              <tbody>
                {ROWS.map(([q, a, b], i) => (
                  <tr key={i} className="border-b border-neutral-300" data-testid={`compare-row-${i}`}>
                    <td className="p-3 font-bold">{q}</td>
                    <td className="p-3 text-neutral-600">{a}</td>
                    <td className="p-3 bg-[#F8F9FA]">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section data-testid="compare-workflow">
          <p className="label-mono text-neutral-500 mb-4">THE 1–12 ANGLE E-COMMERCE DROP, DONE ONCE</p>
          <ol className="grid grid-cols-1 md:grid-cols-4 border-2 border-black divide-y-2 md:divide-y-0 md:divide-x-2 divide-black">
            {STEPS.map((s, i) => (
              <li key={i} className="p-5">
                <s.icon size={28} weight="bold" className={i === 0 ? "text-[#FF2A2A]" : i === 3 ? "text-[#002FA7]" : ""} />
                <h2 className="font-display font-bold tracking-tighter text-base mt-3">{String(i + 1).padStart(2, "0")} · {s.t}</h2>
                <p className="text-xs font-mono text-neutral-700 mt-2 leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="border-2 border-black p-6 space-y-3" data-testid="compare-fair">
          <p className="font-display font-bold tracking-tighter text-lg">WHEN A POST-HOC RENAMER IS THE RIGHT TOOL</p>
          <p className="text-sm font-mono text-neutral-700">
            Be fair to them: if the photos already exist — a back catalogue, a supplier's ZIP, a decade of IMG_ files — you can't
            capture them again, and an AI renamer is the correct fix. Capture-time naming is for the photos you haven't taken yet,
            which for an active store is every drop from today onward.
          </p>
        </section>

        <footer className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between border-t-2 border-black pt-8">
          <p className="font-display font-black tracking-tighter text-2xl">STOP RENAMING. START NAMING.</p>
          <div className="flex gap-3">
            <Link to="/signin" className="btn-flat px-5 py-3 inline-flex items-center gap-2" data-testid="compare-cta">Start Capturing <ArrowRight size={16} weight="bold" /></Link>
            <Link to="/pricing" className="btn-flat-outline px-5 py-3" data-testid="compare-pricing">Pricing</Link>
          </div>
        </footer>
      </article>
    </MarketingFrame>
  );
}
